import type { Server } from 'miragejs';
import { response } from 'mocks/helpers';
import { HTTP_RESPONSE_CODES } from 'mocks/api';
import { t } from '@lingui/macro';
import type { View } from 'types/views';

// Initial views data shared between seeds and reset endpoint
export const initialViewsData = [
  {
    id: 1,
    viewName: 'View 1',
    colorIndicator: '#3B82F6',
    displayOrder: 1,
    isDefault: true,
    isShared: false,
    filterCriteria: { taskName: 1 },
    createdAt: new Date('2024-01-01').toISOString(),
    updatedAt: new Date('2024-01-01').toISOString(),
  },
  {
    id: 2,
    viewName: 'View 2',
    colorIndicator: '#10B981',
    displayOrder: 2,
    isDefault: false,
    isShared: false,
    filterCriteria: { taskName: 2 },
    createdAt: new Date('2024-01-02').toISOString(),
    updatedAt: new Date('2024-01-02').toISOString(),
  },
  {
    id: 3,
    viewName: 'View 3',
    colorIndicator: '#F59E0B',
    displayOrder: 3,
    isDefault: false,
    isShared: false,
    filterCriteria: { taskName: 3 },
    createdAt: new Date('2024-01-03').toISOString(),
    updatedAt: new Date('2024-01-03').toISOString(),
  },
];

// Helper to create initial views in Mirage schema
export const seedInitialViews = (server: Server) => {
  initialViewsData.forEach(viewData => {
    (server as unknown as { create: (type: string, data: unknown) => void }).create(
      'view',
      viewData
    );
  });
};

export const viewsEndpoints = (server: Server) => {
  // Get all views for an entity type
  server.get('/api/views/:entityType', (schema, request) => {
    const views = schema.all('view').models;
    return response(
      request.url,
      { views },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'ok',
        status: 'success',
      }
    );
  });

  // Get a specific view by ID
  server.get('/api/views/:entityType/:viewId', (schema, request) => {
    const { viewId } = request.params;
    const view = schema.find('view', viewId);

    if (!view) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'view_not_found',
        status: 'fail',
        error: {
          details: [
            {
              type: t`not_found`,
              value: t`Could not find a view with the id of ${viewId}`,
            },
          ],
        },
      });
    }

    return response(request.url, view, {
      statusCode: HTTP_RESPONSE_CODES.OK,
      code: 'ok',
      status: 'success',
    });
  });

  // Create a new view
  server.post('/api/views/:entityType', (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const currentViews = schema.all('view').models;

    const newView = (
      schema as unknown as { create: (type: string, data: unknown) => { attrs: View } }
    ).create('view', {
      viewName: body.viewName || `View ${currentViews.length + 1}`,
      colorIndicator: body.colorIndicator || '#3B82F6',
      displayOrder: currentViews.length + 1,
      isDefault: false,
      isShared: body.isShared || false,
      filterCriteria: body.filterCriteria || {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return response(request.url, newView, {
      statusCode: HTTP_RESPONSE_CODES.CREATED,
      code: 'view_created',
      status: 'success',
    });
  });

  // Update a view
  server.put('/api/views/:entityType/:viewId', (schema, request) => {
    const { viewId } = request.params;
    const body = JSON.parse(request.requestBody);
    const view = schema.find('view', viewId);

    if (!view) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'view_not_found',
        status: 'fail',
        error: {
          details: [
            {
              type: t`not_found`,
              value: t`Could not find a view with the id of ${viewId}`,
            },
          ],
        },
      });
    }

    view.update({
      ...body,
      updatedAt: new Date().toISOString(),
    });

    return response(request.url, view, {
      statusCode: HTTP_RESPONSE_CODES.UPDATED,
      code: 'view_updated',
      status: 'success',
    });
  });

  // Reorder views
  server.put('/api/views/:entityType/order', (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const { viewOrders } = body;

    if (viewOrders && Array.isArray(viewOrders)) {
      viewOrders.forEach((order: { id: number; displayOrder: number }) => {
        const view = (
          schema as unknown as {
            find: (
              type: string,
              id: number
            ) => { update: (data: { displayOrder: number }) => void } | null;
          }
        ).find('view', order.id);
        if (view) {
          view.update({ displayOrder: order.displayOrder });
        }
      });
    }

    return response(
      request.url,
      { success: true },
      {
        statusCode: HTTP_RESPONSE_CODES.UPDATED,
        code: 'views_reordered',
        status: 'success',
      }
    );
  });

  // Delete a view
  server.delete('/api/views/:entityType/:viewId', (schema, request) => {
    const { viewId } = request.params;
    const view = schema.find('view', viewId);

    if (!view) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'view_not_found',
        status: 'fail',
        error: {
          details: [
            {
              type: t`not_found`,
              value: t`Could not find a view with the id of ${viewId}`,
            },
          ],
        },
      });
    }

    view.destroy();

    return response(request.url, undefined, {
      statusCode: HTTP_RESPONSE_CODES.NO_CHANGE,
      code: 'view_deleted',
      status: 'success',
    });
  });

  // Setup endpoint for testing - set up a specific number of views
  server.post('/api/views/setup', (schema, request) => {
    const { count } = JSON.parse(request.requestBody);

    // Clear all existing views
    schema.all('view').models.forEach(view => view.destroy());

    // Insert new views into DB
    const views = Array.from({ length: count }, (_, i) => ({
      viewName: `View ${i + 1}`,
      colorIndicator: '#3B82F6',
      displayOrder: i + 1,
      isDefault: i === 0,
      isShared: false,
      filterCriteria: { taskName: i + 1 },
      createdAt: new Date('2024-01-01').toISOString(),
      updatedAt: new Date('2024-01-01').toISOString(),
    }));

    schema.db.views.insert(views);

    return response(
      request.url,
      { success: true, message: `Views set up with ${count} views` },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'views_setup_complete',
        status: 'success',
      }
    );
  });

  // Reset endpoint for testing - restore initial state
  server.post('/api/views/reset', (schema, request) => {
    // Clear all existing views
    schema.all('view').models.forEach(view => view.destroy());

    // Recreate initial 3 views using shared helper
    seedInitialViews(server);

    return response(
      request.url,
      { success: true, message: 'Views reset to initial state' },
      {
        statusCode: HTTP_RESPONSE_CODES.OK,
        code: 'views_reset_complete',
        status: 'success',
      }
    );
  });
};
